'use client';

import { MovieCarouselComponent } from '@/components/movie-carousel/movie-carousel';
import { MoviePosterSkeletonComponent } from '@/components/skeletons/movie-poster-skeleton';
import { useFeaturedCarouselMovies } from '@/hooks/movies/featured-carousel-movies';
import { useMovieIdsByTheater } from '@/hooks/movies/movie-ids-by-theater';
import { useTheaterContext } from '@/hooks/state/theater';
import { MoviePoster } from '@/types/movie-poster';
import { movieToMoviePoster } from '@/utils/movie-helper';
import clsx from 'clsx';
import { useEffect, useState } from 'react';

type FeaturedMoviesCarouselComponentProps = {
  className?: string;
  dataLinkCategory: string;
  nextArrowDataLinkName?: string;
  previousArrowDataLinkName?: string;
  showBasedOnTheater?: boolean;
  title?: string;
  zipCode?: string;
};

export const FeaturedMoviesCarouselComponent = ({
  className,
  dataLinkCategory,
  nextArrowDataLinkName,
  previousArrowDataLinkName,
  showBasedOnTheater = true,
  title,
  zipCode,
}: Readonly<FeaturedMoviesCarouselComponentProps>): React.JSX.Element | null => {
  const [hasInitiallyLoaded, setHasInitiallyLoaded] = useState(false);
  const { data: theater } = useTheaterContext();
  const { data: featuredMovies, isLoading: isFeaturedMoviesLoading } = useFeaturedCarouselMovies();
  const { data: movieIds, isLoading: isMovieIdsLoading } = useMovieIdsByTheater(theater?.theaterId || 0);

  useEffect(() => {
    if (!isFeaturedMoviesLoading && !isMovieIdsLoading) {
      setHasInitiallyLoaded(true);
    }
  }, [isFeaturedMoviesLoading, isMovieIdsLoading]);

  const nowPlayingMovies = movieIds?.nowPlayingMovieIds || [];
  const comingSoonMovies = movieIds?.comingSoonMovieIds || [];
  const fullMovieIdList = [...nowPlayingMovies, ...comingSoonMovies];
  const carouselMovies = showBasedOnTheater ? featuredMovies.filter(movie => fullMovieIdList.includes(movie.cinemarkMovieId || 0)) : featuredMovies;
  const items: MoviePoster[] = carouselMovies.map(movie => movieToMoviePoster(movie, dataLinkCategory, theater));

  if (!hasInitiallyLoaded) {
    return (
      <div className="flex gap-sm">
        {
          [1, 2, 3, 4, 5].map(key =>
            <MoviePosterSkeletonComponent key={key}
              type="carousel"
            />
          )
        }
      </div>
    );
  }

  if (!items?.length) return null;

  return (
    <div className={clsx('flex flex-col gap-ms', className)}>
      {title && <h2 data-test="featured-movies-carousel-title">{title}</h2>}

      <MovieCarouselComponent dataLinkCategory={dataLinkCategory}
        items={items}
        nextArrowDataLinkName={nextArrowDataLinkName}
        previousArrowDataLinkName={previousArrowDataLinkName}
        timezone={theater?.timeZone}
        zipCode={zipCode}
      />
    </div>
  );
};
